// Calibration table types and lookups (no simulation imports, safe for the game bundle).
import type { GripModel } from '../bots/stage-bots';
import { SURFACES } from '../sim/surfaces';
import type { SurfaceId } from '../sim/types';
import table from './calibration.json';

export interface CalibrationTable {
  car: string;
  radii: number[];
  /** Max sustained speed (m/s) per radius, per surface class. */
  gravel: number[];
  tarmac: number[];
  method: string;
}

export const CALIBRATION = table as CalibrationTable;

/** Interpolate the limit speed (m/s) for a radius (log-radius interpolation). */
export function limitFromTable(t: CalibrationTable, surfaceClass: 'gravel' | 'tarmac', r: number): number {
  const arr = surfaceClass === 'tarmac' ? t.tarmac : t.gravel;
  const R = t.radii;
  if (r <= R[0]) return arr[0] * Math.sqrt(r / R[0]);
  if (r >= R[R.length - 1]) return arr[arr.length - 1] * Math.sqrt(r / R[R.length - 1]);
  let i = 0;
  while (i < R.length - 2 && R[i + 1] < r) i++;
  const u = (Math.log(r) - Math.log(R[i])) / (Math.log(R[i + 1]) - Math.log(R[i]));
  return arr[i] + (arr[i + 1] - arr[i]) * u;
}

export const surfaceClass = (s: SurfaceId | string): 'gravel' | 'tarmac' => (s === 'tarmac' || s === 'concrete' ? 'tarmac' : 'gravel');

/** Limit speed for a radius on a surface: calibrated class speed scaled by √(µ_surface / µ_class). */
export function limitSpeed(t: CalibrationTable, surface: SurfaceId, r: number): number {
  const cls = surfaceClass(surface);
  const base = limitFromTable(t, cls, r);
  const ratio = SURFACES[surface].mu / SURFACES[cls].mu;
  return base * Math.sqrt(Math.min(1, ratio));
}

export function gripFromCalibration(t: CalibrationTable = CALIBRATION): GripModel {
  return { latG: (surface, r) => (limitSpeed(t, surface as SurfaceId, Math.min(r, 2000)) ** 2) / (Math.min(r, 2000) * 9.81) };
}
